import { Config } from 'config/envConfig';
import axios from 'axios';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { camelToTitleCase, repoStatsConfig, getDisplayDateTimeParts, RepoStatKey } from '@quyva/common';
import { Table, Tabs } from 'antd';
import { LoadingView, DetailsPanel, CustomTable, Button } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CrawlHistoryTable } from './CrawlHistoryTable';
import { faChartColumn, faExclamationTriangle, faGlobe, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { getUrl } from './pdfDisplay/utils';
import { RepoType } from '@quyva/common';
import { Modal } from '@quyva/shared-ui';
import { DiagnosticsPanel } from './diagnostic_data';
import { faEye, faFile } from '@fortawesome/free-solid-svg-icons';
import { DebugViewerContainer } from './debugViewerContainer';
import { useGlobalCredits } from '../context/GlobalStateContext.js';
import { NoDataView } from './noDataView';


// Any file tied to a crawled URL (the URL's own .md, or an attachment
// downloaded from that page) is already rolled up into that URL's row in
// urlRows -- showing it again here would double-count it in the Overview.
const shouldHideInternetGeneratedFile = (data) => Boolean(data?.associated_website);

const RenderFilesData = ({ filesData, storageType, urlInfoList = [] }) => {
    const isInternet = storageType === RepoType.INTERNET;

    const visibleFilesData = (filesData || []).filter((data) =>
        !isInternet ? true : !shouldHideInternetGeneratedFile(data)
    );

    const fileRows = visibleFilesData.map((data) => {
        const { associated_website, ...rest } = data;
        return rest;
    });

    // For crawled (internet) repos, surface each crawled page as its own row:
    // fileName = URL, fileSize = md file size + its attachments' sizes (bytes),
    // pages = chunks generated + its attachments' pages. Attachments (files whose
    // associated_website matches this URL's md file) are otherwise hidden in the
    // Overview, so both their bytes and their page/chunk counts are rolled up here
    // to make the URL row the true footprint of everything under it. The md file
    // size is read from filesData (matched by md file name), falling back to
    // markdown_size_kb from the crawl info when not found.
    const urlRows = isInternet
        ? (urlInfoList || []).map((entry) => {
              const mdMatch = (filesData || []).find(
                  (f) => f.fileName === entry.md_file_name
              );
              const mdSize =
                  mdMatch?.fileSize ??
                  (entry.markdown_size_kb != null
                      ? Math.round(Number(entry.markdown_size_kb) * 1024)
                      : undefined);

              const attachments = (filesData || []).filter(
                  (f) => f.associated_website === entry.md_file_name
              );

              const attachmentsSize = attachments.reduce(
                  (sum, f) => sum + (Number(f.fileSize) || 0),
                  0
              );

              const attachmentsChunks = attachments.reduce(
                  (sum, f) => sum + (Number(f.pages) || 0),
                  0
              );

              const totalSize =
                  mdSize === undefined && attachmentsSize === 0
                      ? undefined
                      : (mdSize || 0) + attachmentsSize;

              const baseChunks =
                  entry.chunks_generated != null
                      ? Number(entry.chunks_generated)
                      : undefined;

              const totalChunks =
                  baseChunks === undefined && attachmentsChunks === 0
                      ? undefined
                      : (baseChunks || 0) + attachmentsChunks;

              return {
                  fileName: entry.url,
                  fileSize: totalSize ?? '—',
                  pages: totalChunks ?? '—',
              };
          })
        : [];

    const combinedRows = [...fileRows, ...urlRows];
    if (!combinedRows.length) return null;

    const modifiedFilesData = combinedRows.map((row, i) => ({
        'sNo.': `${i + 1}.`,
        ...row,
    }));

    const columns = Object.keys(modifiedFilesData[0]).map((key) => {
        let title = camelToTitleCase(key);
        if (key.toLowerCase() === 'filesize') {
            title = 'File Size (B)';
        } else if (key.toLowerCase() === 'pages') {
            title = isInternet ? 'Pages/Chunks' : 'Pages';
        }

        return {
            title,
            dataIndex: key,
            key,
            width: 120,
            ellipsis: true,
        };
    });

    return (
        <Table
            tableLayout="fixed"
            columns={columns}
            dataSource={modifiedFilesData}
            pagination={{ defaultPageSize: 3 }}
        />
    );
};


const RenderList = ({ repository, storageType, isAdmin, urlInfoList = [] }) => {
    if (!repository) return null;

    if (!Array.isArray(repository.filesData)) return null;
    const isInternet = storageType === RepoType.INTERNET;
    const visibleFilesData = repository.filesData.filter((data) =>
        !isInternet ? true : !shouldHideInternetGeneratedFile(data)
    );

    const hasUrlRows = isInternet && Array.isArray(urlInfoList) && urlInfoList.length > 0;

    if (!visibleFilesData.length && !hasUrlRows) {
        return (
            <div className="w-full">
                <NoDataView text="No files found" icon={faFile} iconColor="sky" />
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="w-full font-semibold text-texts-secondary300 mb-2">
                Files
            </div>

            <RenderFilesData
                filesData={repository.filesData}
                storageType={storageType}
                urlInfoList={urlInfoList}
            />
        </div>
    );
};

const RepoSummaryGrid = ({ repository, storageType, isAdmin }) => {
    const COLUMN_STRUCTURE =
        repoStatsConfig[storageType]?.[isAdmin ? 'admin' : 'user'] ||
        repoStatsConfig.DEFAULT[isAdmin ? 'admin' : 'user'];

    if (!repository) return null;

    const buildItem = (key) => {
        if (!isAdmin && (key === RepoStatKey.PROCESSING_TIME || key === RepoStatKey.USED_CREDITS)) return null;
        if (repository[key] === undefined) return null;

        const value = repository[key];
        if (value === null || value === undefined) return null;
        if (typeof value === 'object') return null;

        let displayValue = value;

        if (key === RepoStatKey.CREATED_AT || key === RepoStatKey.UPDATED_AT) {
            const { date, time } = getDisplayDateTimeParts(value);
            displayValue = `${date}, ${time}`;
        } else if (key === RepoStatKey.PROCESSING_TIME) {
            displayValue = `${Number(value).toFixed(2)} s`;
        } else if (key === RepoStatKey.TOTAL_SIZE) {
            displayValue = `${value} B`;
        }

        return {
            label:
                storageType === RepoType.INTERNET && key === RepoStatKey.TOTAL_PAGES
                    ? 'Total Chunks'
                    : key === RepoStatKey.PROCESSING_TIME
                    ? 'Total Processing Time'
                    : camelToTitleCase(key),
            value: String(displayValue),
        };
    };




    return (
        <div className="w-full bg-backgrounds-white rounded-lg border p-3 mb-3">
            <div className="flex flex-col md:flex-row gap-3 w-full">
                {COLUMN_STRUCTURE.map((col, colIdx) => (
                    <div key={colIdx} className="flex flex-col gap-2 w-full md:flex-1">
                        {col
                            .map(buildItem)
                            .filter(Boolean)
                            .map((item) => (
                                <div
                                    key={item.label}
                                    className="px-3 py-2 rounded-md border bg-backgrounds-secondary flex items-center justify-between"
                                >
                                    <div className="text-sm font-medium text-texts-secondary300">
                                        {item.label}
                                    </div>
                                    <div className="text-right">
                                        {item.value}
                                    </div>
                                </div>
                            ))}
                    </div>
                ))}
            </div>
        </div>
    );
};


export const RepositoryStats = ({
    repository,
    filesListData,
    mode,
}) => {
    const user = JSON.parse(localStorage.getItem('current'));

    const [repoStat, setRepoStat] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedCrawlRow, setSelectedCrawlRow] = useState(null);
    const [crawlState, setCrawlState] = useState('loading'); // 'loading' | 'data' | 'empty' | 'error'
    const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
    const [debugFileName, setDebugFileName] = useState(null);
    const [debugWebsiteUrl, setDebugWebsiteUrl] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [urlInfoList, setUrlInfoList] = useState([]);
    const { isAdmin } = useGlobalCredits();
    const isInternetRepo = repository.storage_type === RepoType.INTERNET;

    const getRepoStats = async () => {
        try {
            setIsLoading(true);
            const apiUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/folder-stats?folder_name=${repository.name}&user=${repository.email}&mode=${mode}`
            );
            const response = await axios.get(apiUrl);
            setRepoStat(response.data);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error.message);
            setErrorMessage(error.message);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        async function fetchData() {
            await getRepoStats();
        }

        fetchData();
    }, [repository]);

    useEffect(() => {
        setSelectedCrawlRow(null);
    }, [repository?.repoid]);

    useEffect(() => {
        setActiveTab('overview');
    }, [repository?.repoid]);

    // For internet (crawl) repos, fetch per-URL crawl info so the Overview files
    // table can show each crawled page (URL, md file size, chunks generated).
    useEffect(() => {
        if (!isInternetRepo || !repository?.repoid) {
            setUrlInfoList([]);
            return;
        }

        let cancelled = false;
        const fetchUrlInfo = async () => {
            try {
                const apiUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/crawl/crawl_timestamps?repo_id=${encodeURIComponent(repository.repoid)}`
                );
                const res = await axios.get(apiUrl);
                const data = res.data;
                if (!cancelled) {
                    setUrlInfoList(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error('Error fetching crawl url info:', error?.message);
                if (!cancelled) {
                    setUrlInfoList([]);
                }
            }
        };

        fetchUrlInfo();
        return () => {
            cancelled = true;
        };
    }, [repository?.repoid, isInternetRepo]);

    if (isLoading) {
        return (
            <div className='w-full h-96 max-h-96 flex justify-center items-center'>
                <LoadingView loadingText={'Loading stats ...'} />
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className='w-full p-3 h-96 max-h-96 overflow-auto flex flex-col justify-center items-center text-buttons-alert400 font-semibold'>
                <FontAwesomeIcon icon={faExclamationTriangle} size='2x' />
                <div className='mt-2'>{errorMessage}</div>
            </div>
        );
    }

    return (
        <div className='w-[92vw] max-w-[1400px] h-[80vh] max-h-[80vh] bg-backgrounds-slate rounded-xl overflow-auto'>
            <div className='w-full h-full min-w-0 flex flex-col px-4 py-4'>
                <div className='flex-1 min-h-0'>
                    <RepoSummaryGrid
                        repository={repoStat}
                        storageType={repository.storage_type}
                        isAdmin={isAdmin}
                    />
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        className='h-full repository-stats-tabs'
                        items={[
                            {
                                key: 'overview',
                                label: (
                                    <span>
                                        <FontAwesomeIcon icon={faChartColumn} className='mr-2' />
                                        Overview
                                    </span>
                                ),
                                children: (
                                    <div className='min-h-0 pr-1'>
                                        <RenderList
                                            repository={repoStat}
                                            storageType={repository.storage_type}
                                            isAdmin={isAdmin}
                                            urlInfoList={urlInfoList}
                                        />
                                    </div>
                                ),
                            },
                            ...(isInternetRepo
                                ? [{
                                    key: 'crawl-history',
                                    label: (
                                        <span>
                                            <FontAwesomeIcon icon={faGlobe} className='mr-2' />
                                            URL Details
                                        </span>
                                    ),
                                    children: (
                                        <div className='h-[calc(80vh-185px)] min-h-0 overflow-hidden'>
                                            {/* CrawlHistoryTable stays mounted so it fetches and reports its
                                                state; the two-column layout is hidden until there is crawl
                                                history to show, so the whole tab shows one empty-state instead
                                                of an empty crawl table + empty files table. */}
                                            <div className={crawlState === 'data' ? 'w-full h-full flex gap-3 overflow-hidden' : 'hidden'}>
                                                <div className='flex-1 min-w-0 w-0 overflow-auto'>
                                                    <CrawlHistoryTable
                                                        repoId={repository.repoid}
                                                        filesListData={filesListData}
                                                        repository={repoStat}
                                                        repoMeta={repository}
                                                        mode={mode}
                                                        selectedRowKey={selectedCrawlRow?.key}
                                                        onSelectRow={(row) => setSelectedCrawlRow(row)}
                                                        setInitialRow={(row) => {
                                                            setSelectedCrawlRow((prev) => prev ?? row);
                                                        }}
                                                        onStateChange={setCrawlState}
                                                    />
                                                </div>

                                                <div className='flex-none w-[400px] max-w-[400px] h-full overflow-hidden'>
                                                    <DetailsPanel
                                                        title={'Attachments'}
                                                        subtitle={selectedCrawlRow?.url || ''}
                                                        hideCloseButton={true}
                                                        onClose={() => {}}
                                                        contentClassName='flex-1 min-h-0'
                                                    >
                                                        <UrlFilesTable
                                                            repository={repoStat}
                                                            crawlRow={selectedCrawlRow}
                                                            onViewFile={(fileName, websiteUrl) => {
                                                                setDebugFileName(fileName);
                                                                setDebugWebsiteUrl(websiteUrl || '');
                                                                setIsDebugModalOpen(true);
                                                            }}
                                                        />
                                                    </DetailsPanel>
                                                </div>
                                            </div>

                                            {crawlState !== 'data' && (
                                                crawlState === 'loading' ? (
                                                    <LoadingView />
                                                ) : (
                                                    <NoDataView
                                                        text={crawlState === 'error' ? 'Failed to fetch crawl history.' : 'No crawl history available.'}
                                                        icon={faGlobe}
                                                        iconColor='sky'
                                                    />
                                                )
                                            )}
                                        </div>
                                    ),
                                }]
                                : []),
                            ...(isAdmin
                                ? [{
                                    key: 'token-diagnostics',
                                    label: (
                                        <span>
                                            <FontAwesomeIcon icon={faGaugeHigh} className='mr-2' />
                                            Token Diagnostics
                                        </span>
                                    ),
                                    children: (
                                        <DiagnosticsPanel diagnostics={repoStat?.display_diagnostics} wrapperClassName='min-h-0 pr-1' />
                                    ),
                                }]
                                : []),
                        ]}
                    />
                </div>
                {isDebugModalOpen && debugFileName && (
                    <Modal
                        title={'File viewer'}
                        titleIcon={faFile}
                        iconColor={'sky'}
                        onClose={() => setIsDebugModalOpen(false)}
                        zIndex={9999}
                    >
                        <DebugViewerContainer
                            repoId={repository.repoid}
                            email={repository.email}
                            folderName={repository.name}
                            fileName={debugFileName}
                            websiteUrl={debugWebsiteUrl}
                            mode={mode}
                            isAdmin={repository.is_admin}
                        />
                    </Modal>
                )}
            </div>
        </div>
    );
};

const UrlFilesTable = ({ repository, crawlRow, onViewFile }) => {
    const mdFileName = crawlRow?.md_file_name;
    const repoFiles = Array.isArray(repository?.filesData)
        ? repository.filesData
        : [];

    const rows = (repoFiles || [])
        .filter((f) => {
            const assoc = f?.associated_website || '';
            if (!mdFileName) {
                return false;
            }
            return assoc === mdFileName;
        })
        .map((f, index) => ({
            key: index,
            fileName: f?.fileName || '—',
            fileSize: f?.fileSize ?? '—',
            pages: f?.pages ?? '—',
        }));

    const columns = [
        {
            title: 'Name',
            dataIndex: 'fileName',
            key: 'fileName',
            ellipsis: true,
        },
        {
            title: 'Size',
            dataIndex: 'fileSize',
            key: 'fileSize',
            width: 80,
        },
        {
            title: 'Pages',
            dataIndex: 'pages',
            key: 'pages',
            width: 80,
            align: 'center'
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="icon"
                    icon={faEye}
                    className="text-texts-tertiary transition-all duration-200 hover:text-texts-tertiary400 hover:scale-110"
                    onClick={() =>
                        onViewFile?.(record.fileName, crawlRow?.md_file_name)
                    }
                />
            ),
        },
    ];

    return (
        <div className="w-full h-full overflow-y-auto">
            <CustomTable
                tableLayout="fixed"
                columns={columns}
                dataSource={rows}
                pagination={false}
                rowKey="key"
                className="[&_.ant-table_table]:!border-separate [&_.ant-table-thead]:sticky [&_.ant-table-thead]:top-0 [&_.ant-table-thead]:z-10 [&_.ant-table-thead>tr>th]:bg-backgrounds-white"
            />
        </div>

    );
};